/**
 * Connection management service
 *
 * Handles OAuth connections to external platforms (Square, QuickBooks, Shopify)
 * Supports state transitions (pause/unpause/archive/restore) and OAuth flows
 *
 * Based on CONNECTION_STATE_INTERFACE.md specification (Model B)
 */

import { apiService } from './apiService'
import type { ApiResponse } from '@/types/api.types'
import type {
  Connection,
  CreateConnectionRequest,
  OAuthInitiateRequest,
  OAuthInitiateResponse,
  OAuthCompleteRequest,
  OAuthCompleteResponse,
  ReconnectRequest,
  ReconnectResponse,
  TestConnectionResult,
} from '@/types/connection.types'

/**
 * Connection management service
 * Handles CRUD operations and state transitions for OAuth connections
 */
class ConnectionService {
  private readonly basePath = '/connections'

  /**
   * List all connections for current organization
   *
   * Note: By default excludes soft-deleted connections unless include_deleted=true
   * query parameter is used. Archived connections (state='archived') are included
   * by default - use state filter to exclude them.
   *
   * @returns List of connections with credentialStatus, state, and health info
   *
   * @example
   * ```typescript
   * const response = await connectionService.list()
   * if (response.success) {
   *   const connections = response.data
   * }
   * ```
   */
  async list(): Promise<ApiResponse<Connection[]>> {
    return await apiService.get<Connection[]>(this.basePath)
  }

  /**
   * Get connection by UID
   *
   * @param uid - Connection UID
   * @returns Connection details
   *
   * @example
   * ```typescript
   * const response = await connectionService.get('770e8400-e29b-41d4-a716-446655440000')
   * ```
   */
  async get(uid: string): Promise<ApiResponse<Connection>> {
    return await apiService.get<Connection>(`${this.basePath}/${uid}`)
  }

  /**
   * Create new connection (does NOT initiate OAuth)
   *
   * Creates connection in 'pending_auth' credential status.
   * Use initiateOAuth() after creation to start OAuth flow.
   *
   * @param request - Connection details with platform and environment
   * @returns Created connection
   *
   * @example
   * ```typescript
   * const response = await connectionService.create({
   *   name: 'Square Production',
   *   platform: 'square',  // 'square-sandbox' for sandbox
   *   description: 'Main POS connection'
   * })
   * if (response.success) {
   *   // Now initiate OAuth flow
   *   await connectionService.initiateOAuth({
   *     connectionUid: response.data.uid,
   *     redirectUri: window.location.origin + '/oauth/callback'
   *   })
   * }
   * ```
   */
  async create(request: CreateConnectionRequest): Promise<ApiResponse<Connection>> {
    return await apiService.post<Connection>(this.basePath, request)
  }

  /**
   * Update connection details
   *
   * @param uid - Connection UID
   * @param updates - Fields to update (name, description)
   * @param version - Current version for optimistic locking
   * @returns Updated connection
   *
   * @example
   * ```typescript
   * const response = await connectionService.update(
   *   '770e8400-e29b-41d4-a716-446655440000',
   *   { name: 'Square Production - Main Store' },
   *   5  // Current version
   * )
   * ```
   */
  async update(
    uid: string,
    updates: Partial<Pick<Connection, 'name' | 'description'>>,
    version: number
  ): Promise<ApiResponse<Connection>> {
    // Include version in body for optimistic locking (backend extracts it)
    return await apiService.put<Connection>(
      `${this.basePath}/${uid}`,
      { ...updates, version }
    )
  }

  /**
   * Soft delete connection (sets deletedAt timestamp)
   *
   * Standard bitvoid soft delete - INDEPENDENT of archive state.
   * A connection can be archived but not deleted, deleted but not archived,
   * or both archived and deleted.
   *
   * Note: DELETE returns 204 No Content with null data body.
   *
   * @param uid - Connection UID
   * @returns Success response (data is null for 204 responses)
   *
   * @example
   * ```typescript
   * const response = await connectionService.delete('770e8400-e29b-41d4-a716-446655440000')
   * if (response.success) {
   *   console.log('Connection deleted')
   * }
   * ```
   */
  async delete(uid: string): Promise<ApiResponse<null>> {
    return await apiService.delete<null>(`${this.basePath}/${uid}`)
  }

  /**
   * Restore soft-deleted connection (clears deletedAt)
   *
   * Standard bitvoid undelete - INDEPENDENT of archive state.
   * If connection was also archived, it remains archived after undelete.
   *
   * @param uid - Connection UID
   * @returns Restored connection with deletedAt cleared
   *
   * @example
   * ```typescript
   * const response = await connectionService.undelete('770e8400-e29b-41d4-a716-446655440000')
   * ```
   */
  async undelete(uid: string): Promise<ApiResponse<Connection>> {
    // Note: Backend endpoint pattern TBD - may be PUT with undelete action
    return await apiService.post<Connection>(`${this.basePath}/${uid}/undelete`)
  }

  // ========================================
  // State Transition Methods (State Machine)
  // ========================================

  /**
   * Pause connection (active → paused)
   *
   * User temporarily disables connection. Stops data syncing but keeps
   * credentials valid. Available to user, admin, or owner roles.
   *
   * @param uid - Connection UID
   * @returns Updated connection with state='paused'
   *
   * @throws InvalidTransitionError if connection not in 'active' state
   * @throws PermissionDeniedError if user lacks required role
   *
   * @example
   * ```typescript
   * const response = await connectionService.pause('770e8400-e29b-41d4-a716-446655440000')
   * if (response.success) {
   *   console.log('Connection paused')
   * } else {
   *   // Check for specific error codes
   *   const invalidTransition = response.errors?.find(e => e.code === 'INVALID_STATE_TRANSITION')
   *   if (invalidTransition) {
   *     console.error('Cannot pause from current state:', invalidTransition.details)
   *   }
   * }
   * ```
   */
  async pause(uid: string): Promise<ApiResponse<Connection>> {
    return await apiService.post<Connection>(`${this.basePath}/${uid}/pause`)
  }

  /**
   * Unpause connection (paused → active)
   *
   * User re-enables paused connection. Resumes data syncing.
   * Available to user, admin, or owner roles.
   *
   * @param uid - Connection UID
   * @returns Updated connection with state='active'
   *
   * @throws InvalidTransitionError if connection not in 'paused' state
   * @throws PermissionDeniedError if user lacks required role
   *
   * @example
   * ```typescript
   * const response = await connectionService.unpause('770e8400-e29b-41d4-a716-446655440000')
   * ```
   */
  async unpause(uid: string): Promise<ApiResponse<Connection>> {
    return await apiService.post<Connection>(`${this.basePath}/${uid}/unpause`)
  }

  /**
   * Archive connection (active/paused → archived)
   *
   * Hides connection from default list view by setting state='archived'.
   * This is INDEPENDENT of soft delete - a connection can be archived but
   * not deleted, or both archived and deleted.
   *
   * Restricted to admin or owner roles only (hides data from other users).
   *
   * @param uid - Connection UID
   * @returns Updated connection with state='archived'
   *
   * @throws InvalidTransitionError if connection already archived
   * @throws PermissionDeniedError if user lacks admin/owner role
   *
   * @example
   * ```typescript
   * const response = await connectionService.archive('770e8400-e29b-41d4-a716-446655440000')
   * if (response.success) {
   *   console.log('Connection archived - hidden from default list')
   * }
   * ```
   */
  async archive(uid: string): Promise<ApiResponse<Connection>> {
    return await apiService.post<Connection>(`${this.basePath}/${uid}/archive`)
  }

  /**
   * Restore archived connection (archived → active)
   *
   * Shows connection in default list again by setting state='active'.
   * This is INDEPENDENT of soft delete restoration - only changes state field.
   * If connection was also soft deleted, use undelete() separately.
   *
   * Restricted to admin or owner roles only.
   *
   * @param uid - Connection UID
   * @returns Updated connection with state='active'
   *
   * @throws InvalidTransitionError if connection not archived
   * @throws PermissionDeniedError if user lacks admin/owner role
   *
   * @example
   * ```typescript
   * const response = await connectionService.restore('770e8400-e29b-41d4-a716-446655440000')
   * if (response.success) {
   *   console.log('Connection restored - visible in default list')
   * }
   * ```
   */
  async restore(uid: string): Promise<ApiResponse<Connection>> {
    return await apiService.post<Connection>(`${this.basePath}/${uid}/restore`)
  }

  // ========================================
  // OAuth Flow Methods
  // ========================================

  /**
   * Initiate OAuth 2.0 flow with PKCE
   *
   * Generates authorization URL and CSRF state token. Frontend must:
   * 1. Store state in sessionStorage for validation
   * 2. Redirect user to authorizationUrl
   * 3. Platform redirects back to redirectUri with code and state
   * 4. Validate state matches before processing callback
   *
   * SECURITY NOTE: code_verifier is NEVER returned to frontend.
   * Backend stores it server-side and retrieves during token exchange.
   *
   * @param request - Connection UID and redirect URI
   * @returns Authorization URL and CSRF state token
   *
   * @example
   * ```typescript
   * const response = await connectionService.initiateOAuth({
   *   connectionUid: '770e8400-e29b-41d4-a716-446655440000',
   *   redirectUri: window.location.origin + '/oauth/callback'
   * })
   *
   * if (response.success) {
   *   // Store state for CSRF validation
   *   sessionStorage.setItem('oauth_state', response.data.state)
   *
   *   // Redirect to platform OAuth page
   *   window.location.href = response.data.authorizationUrl
   * }
   * ```
   */
  async initiateOAuth(request: OAuthInitiateRequest): Promise<ApiResponse<OAuthInitiateResponse>> {
    return await apiService.post<OAuthInitiateResponse>(
      `${this.basePath}/oauth/initiate`,
      request
    )
  }

  /**
   * Re-initiate OAuth flow for expired connection
   *
   * Similar to initiateOAuth but for existing connections with expired credentials.
   * Use when credentialStatus === 'expired' and user wants to reconnect.
   *
   * @param uid - Connection UID
   * @param request - Redirect URI for OAuth callback
   * @returns Authorization URL and CSRF token
   *
   * @example
   * ```typescript
   * const response = await connectionService.reconnect(
   *   '770e8400-e29b-41d4-a716-446655440000',
   *   { redirectUri: window.location.origin + '/oauth/callback' }
   * )
   *
   * if (response.success) {
   *   // Note: Field names differ from initiate (authUrl vs authorizationUrl)
   *   sessionStorage.setItem('oauth_state', response.data.csrfToken)
   *   window.location.href = response.data.authUrl
   * }
   * ```
   */
  async reconnect(uid: string, request: ReconnectRequest): Promise<ApiResponse<ReconnectResponse>> {
    return await apiService.post<ReconnectResponse>(
      `${this.basePath}/${uid}/reconnect`,
      request
    )
  }

  /**
   * Complete OAuth flow with PKCE code_verifier
   *
   * Called by OAuthCallbackPage after receiving redirect from platform.
   * Frontend retrieves code_verifier from sessionStorage and sends to backend.
   *
   * SECURITY: This is the OAuth 2.0 PKCE-compliant flow:
   * 1. Platform redirects to backend callback with code and state
   * 2. Backend validates state and redirects to frontend
   * 3. Frontend retrieves code_verifier from sessionStorage
   * 4. Frontend calls this method with code, state, and code_verifier
   * 5. Backend exchanges code for tokens using PKCE
   *
   * @param request - OAuth complete request with code, state, and code_verifier
   * @returns Connection details after successful token exchange
   *
   * @example
   * ```typescript
   * // In OAuthCallbackPage
   * const code = urlParams.get('code')
   * const state = urlParams.get('state')
   * const codeVerifier = sessionStorage.getItem('oauth_code_verifier')
   *
   * const response = await connectionService.completeOAuth({
   *   code,
   *   state,
   *   codeVerifier
   * })
   *
   * if (response.success) {
   *   // Clear sessionStorage and show success
   *   sessionStorage.removeItem('oauth_code_verifier')
   *   sessionStorage.removeItem('oauth_state')
   * }
   * ```
   */
  async completeOAuth(request: OAuthCompleteRequest): Promise<ApiResponse<OAuthCompleteResponse>> {
    return await apiService.post<OAuthCompleteResponse>(
      `${this.basePath}/oauth/complete`,
      request
    )
  }

  // ========================================
  // Token Management Methods
  // ========================================

  /**
   * Refresh OAuth tokens for a connection
   *
   * Manually triggers token refresh before expiry. Useful for:
   * - Demonstrating Square certification compliance (7-day refresh)
   * - Testing token refresh mechanism
   * - Proactively refreshing tokens that will expire soon
   *
   * @param uid - Connection UID
   * @returns Updated connection with refreshed token info
   *
   * @example
   * ```typescript
   * const response = await connectionService.refresh('770e8400-e29b-41d4-a716-446655440000')
   * if (response.success) {
   *   console.log('Token refreshed, expires:', response.data.tokenExpiresAt)
   * }
   * ```
   */
  async refresh(uid: string): Promise<ApiResponse<Connection>> {
    return await apiService.post<Connection>(`${this.basePath}/${uid}/refresh`)
  }

  /**
   * Test connection health
   *
   * Verifies connection can communicate with platform API.
   * Updates healthStatus and lastTestedAt fields.
   *
   * @param uid - Connection UID
   * @returns Test result with health status and account info
   *
   * @example
   * ```typescript
   * const response = await connectionService.test('770e8400-e29b-41d4-a716-446655440000')
   * if (response.success) {
   *   console.log('Health status:', response.data.healthStatus)
   *   console.log('Account info:', response.data.accountInfo)
   * }
   * ```
   */
  async test(uid: string): Promise<ApiResponse<TestConnectionResult>> {
    return await apiService.post<TestConnectionResult>(`${this.basePath}/${uid}/test`)
  }

  /**
   * Validate OAuth state parameter (CSRF protection)
   *
   * CRITICAL: Frontend MUST validate state before processing OAuth callback.
   * This prevents CSRF attacks where attacker tricks user into authorizing
   * attacker's connection.
   *
   * @param receivedState - State parameter from OAuth callback URL
   * @param storedState - State stored in sessionStorage during initiation
   * @returns true if states match, false otherwise
   *
   * @example
   * ```typescript
   * // In OAuth callback page
   * const urlParams = new URLSearchParams(window.location.search)
   * const receivedState = urlParams.get('state')
   * const storedState = sessionStorage.getItem('oauth_state')
   *
   * if (!connectionService.validateOAuthState(receivedState, storedState)) {
   *   console.error('CSRF validation failed - possible attack')
   *   sessionStorage.removeItem('oauth_state')
   *   return  // STOP PROCESSING
   * }
   *
   * // State validated - proceed with backend callback
   * sessionStorage.removeItem('oauth_state')
   * ```
   */
  validateOAuthState(receivedState: string | null, storedState: string | null): boolean {
    if (!receivedState || !storedState) {
      console.error('OAuth state validation failed: missing state parameter')
      return false
    }

    if (receivedState !== storedState) {
      console.error('OAuth state validation failed: state mismatch')
      return false
    }

    return true
  }
}

/**
 * Connection service singleton
 * Use this instance for all connection operations
 */
export const connectionService = new ConnectionService()
